<script setup lang="ts">
import PlanetHeaderVideo from '@/components/Elements/Videos/PlanetHeaderVideo.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import ToggleSwitch from '@/components/Elements/ToggleSwitch/ToggleSwitch.vue'
import { useBattleSimulatorStore } from '@/stores/battleSimulatorStore'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const battleSimulatorStore = useBattleSimulatorStore()

const isPopulationInvalid = computed(() => battleSimulatorStore.planetPopulation < 1)
const isEnergyInvalid = computed(() => battleSimulatorStore.planetEnergy < 0)
const isVshipsInvalid = computed(() => battleSimulatorStore.planetVships < 0)
const isShieldsInvalid = computed(() => battleSimulatorStore.planetShields < 0)
</script>

<template>
  <div class="mt-6">
    <div class="planet-header rounded-t relative overflow-hidden">
      <PlanetHeaderVideo :type="3" class="absolute inset-0 w-full h-full object-cover" />
      <div class="absolute inset-0 header-fade"></div>
      <span class="text-xl text-white relative z-10 p-2 block">{{ t('command.battleSimulator.defendingPlanet.title') }}</span>
    </div>
    <div class="bg-slate-800/40 p-3 rounded-b">
      <div class="flex items-center gap-4">
        <div class="relative w-24">
          <div class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
            <GenericIcon type="person" :tooltip="t('command.battleSimulator.defendingPlanet.population')" />
          </div>
          <input
            v-model.number="battleSimulatorStore.planetPopulation"
            type="number"
            min="1"
            class="w-full bg-slate-800 text-slate-300 rounded pl-8 pr-2 py-1 text-center"
            :class="{ 'ring-1 ring-red-500': isPopulationInvalid }"
          />
        </div>
        <div class="flex-1 flex items-center gap-2">
          <GenericIcon type="soldier" :tooltip="t('command.battleSimulator.defendingPlanet.troops')" />
          <input
            v-model.number="battleSimulatorStore.troopPercentage"
            type="range"
            min="0"
            max="100"
            class="flex-1 accent-blue-500"
          />
          <span class="text-slate-300 w-12 text-right">{{ battleSimulatorStore.troopPercentage }}%</span>
        </div>
        <div class="text-slate-300 w-28 text-right">
          <span class="font-semibold">{{ battleSimulatorStore.planetTroops }}</span> {{ t('command.battleSimulator.defendingPlanet.troopsLabel') }}
        </div>
      </div>
      <div class="flex items-center gap-4 mt-3">
        <div class="relative w-24">
          <div class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
            <GenericIcon type="energy_storage" :tooltip="t('command.battleSimulator.defendingPlanet.maxEnergy')" />
          </div>
          <input
            v-model.number="battleSimulatorStore.planetEnergy"
            type="number"
            min="0"
            class="w-full bg-slate-800 text-slate-300 rounded pl-8 pr-2 py-1 text-center"
            :class="{ 'ring-1 ring-red-500': isEnergyInvalid }"
          />
        </div>
        <div class="flex-1 flex items-center gap-2">
          <GenericIcon type="energy_storage" :tooltip="t('command.battleSimulator.defendingPlanet.shieldCapacity')" />
          <input
            v-model.number="battleSimulatorStore.shieldPercentage"
            type="range"
            min="0"
            max="100"
            class="flex-1 accent-blue-500"
          />
          <span class="text-slate-300 w-12 text-right">{{ battleSimulatorStore.shieldPercentage }}%</span>
        </div>
        <div class="text-slate-300 w-28 text-right">
          <span class="font-semibold">{{ battleSimulatorStore.currentShieldEnergy }}</span> {{ t('command.battleSimulator.defendingPlanet.shields') }}
        </div>
      </div>
      <div class="flex items-center gap-4 mt-3">
        <div class="relative w-24">
          <div class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
            <GenericIcon type="attack_against_ships" :tooltip="t('command.battleSimulator.defendingPlanet.virtualShips')" />
          </div>
          <input
            v-model.number="battleSimulatorStore.planetVships"
            type="number"
            min="0"
            class="w-full bg-slate-800 text-slate-300 rounded pl-8 pr-2 py-1 text-center"
            :class="{ 'ring-1 ring-red-500': isVshipsInvalid }"
          />
        </div>
        <div class="relative w-24">
          <div class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
            <GenericIcon type="shields" :tooltip="t('command.shipDesigns.tooltips.shields')" />
          </div>
          <input
            v-model.number="battleSimulatorStore.planetShields"
            type="number"
            min="0"
            class="w-full bg-slate-800 text-slate-300 rounded pl-8 pr-2 py-1 text-center"
            :class="{ 'ring-1 ring-red-500': isShieldsInvalid }"
          />
        </div>
        <div class="flex items-center gap-2 text-slate-300">
          <ToggleSwitch v-model="battleSimulatorStore.planetFocusFire" class="mb-2" />
          <span>{{ t('command.battleSimulator.defendingPlanet.focusFireMinister') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.planet-header {
  height: 8rem;
}

.header-fade {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 50%);
}
</style>
